import { useEffect, useState } from "react";
import axios from "axios";
import { X, Plus, TShirt, Sneaker, NotePencil, Storefront, Sparkle, Star, StarFour, ArrowLeft } from "@phosphor-icons/react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { SIZE_FIELD_BY_KIND } from "@/lib/sizes";
import StoryComposer from "@/components/StoryComposer";
import OfflineSpotComposer from "@/components/OfflineSpotComposer";
import DuplicateLinkNotice from "@/components/DuplicateLinkNotice";
import { track } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fireConfetti = () => {
  const duration = 1200;
  const end = Date.now() + duration;
  const colors = ["#2D302A", "#6B6A68", "#C9C2B5", "#E4E1D9"];

  // Two side-bursts for a soft, on-brand confetti shower
  confetti({
    particleCount: 60,
    spread: 70,
    origin: { y: 0.7 },
    colors,
    scalar: 0.9,
  });

  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.8 },
      colors,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.8 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
};

// Fit ratings depend on what's being shared. Values stay 'short'|'perfect'|'long'
// so the backend is unchanged — only the wording differs for shoes.
const FIT_OPTIONS_BY_KIND = {
  clothing: [
    { value: "short", label: "A little short, but okay" },
    { value: "perfect", label: "Perfect fit" },
    { value: "long", label: "A little long, but okay" },
  ],
  shoes: [
    { value: "short", label: "A little tight, but okay" },
    { value: "perfect", label: "Perfect fit" },
    { value: "long", label: "A little loose, but okay" },
  ],
};

// The "what are you sharing?" chooser cards.
const KIND_OPTIONS = [
  {
    value: "clothing",
    label: "A Fit",
    hint: "Shirts, jeans, jackets…",
    Icon: TShirt,
  },
  {
    value: "shoes",
    label: "Shoes",
    hint: "Sneakers, boots, loafers…",
    Icon: Sneaker,
  },
  {
    value: "offline_spot",
    label: "An Offline Spot",
    hint: "A real store where it fit…",
    Icon: Storefront,
  },
  {
    value: "story",
    label: "Your Story",
    hint: "What it's like being tall…",
    Icon: NotePencil,
    full: true,
  },
];

const makeEntry = () => ({
  id: Math.random().toString(36).slice(2),
  link: "",
  fit: "",
});

// Soft, storybook sparkles that float around the modal card. Purely
// decorative (aria-hidden, pointer-events-none) and honours reduced-motion.
const FairyDecor = () => (
  <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
    <style>{`
      @keyframes nosTwinkle { 0%,100%{opacity:.2;transform:scale(.8)} 50%{opacity:.9;transform:scale(1.08)} }
      @keyframes nosFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
      @keyframes nosDrift { 0%,100%{transform:translate(0,0)} 50%{transform:translate(6px,-8px)} }
      @media (prefers-reduced-motion: reduce){
        .nos-sparkle{animation:none !important}
      }
    `}</style>
    <div className="nos-sparkle absolute -top-4 -left-4" style={{ animation: "nosFloat 5s ease-in-out infinite" }}>
      <Sparkle size={30} weight="fill" color="#C9C2B5" style={{ animation: "nosTwinkle 3.2s ease-in-out infinite" }} />
    </div>
    <div className="nos-sparkle absolute -top-6 right-6" style={{ animation: "nosDrift 6s ease-in-out infinite" }}>
      <StarFour size={18} weight="fill" color="#B6ADA5" style={{ animation: "nosTwinkle 2.6s ease-in-out .4s infinite" }} />
    </div>
    <div className="nos-sparkle absolute top-10 -right-4" style={{ animation: "nosFloat 4.4s ease-in-out .6s infinite" }}>
      <Star size={16} weight="fill" color="#C9C2B5" style={{ animation: "nosTwinkle 3s ease-in-out infinite" }} />
    </div>
    <div className="nos-sparkle absolute -bottom-4 -left-3" style={{ animation: "nosDrift 5.6s ease-in-out .3s infinite" }}>
      <StarFour size={22} weight="fill" color="#B6ADA5" style={{ animation: "nosTwinkle 2.8s ease-in-out infinite" }} />
    </div>
    <div className="nos-sparkle absolute -bottom-5 right-8" style={{ animation: "nosFloat 5.2s ease-in-out .5s infinite" }}>
      <Sparkle size={24} weight="fill" color="#C9C2B5" style={{ animation: "nosTwinkle 3.4s ease-in-out .2s infinite" }} />
    </div>
    <div className="nos-sparkle absolute bottom-16 -right-3" style={{ animation: "nosTwinkle 2.4s ease-in-out infinite" }}>
      <Star size={12} weight="fill" color="#B6ADA5" />
    </div>
    <div className="nos-sparkle absolute top-1/3 -left-5" style={{ animation: "nosTwinkle 2.9s ease-in-out .7s infinite" }}>
      <StarFour size={12} weight="fill" color="#C9C2B5" />
    </div>
  </div>
);

const ProductSubmissionForm = ({ open, onClose, onSuccess, user, onUserUpdate }) => {
  useBodyScrollLock(open);
  // First the user picks what they're sharing (clothing vs shoes); until then
  // `kind` is null and we show the chooser instead of the form.
  const [kind, setKind] = useState(null);
  // The sharer's own measurement (waist for clothing, shoe size for shoes).
  // Asked once, then pre-filled from the profile on later shares.
  const [size, setSize] = useState("");
  // Users can share several products at once — one entry per link, each with
  // its own fit rating (fit is inherently per-product).
  const [entries, setEntries] = useState([makeEntry()]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fitOptions = FIT_OPTIONS_BY_KIND[kind] || FIT_OPTIONS_BY_KIND.clothing;
  const isShoes = kind === "shoes";
  const sizeField = SIZE_FIELD_BY_KIND[kind] || SIZE_FIELD_BY_KIND.clothing;

  // form_start / form_submit are GA4's documented form pair (the same names its
  // enhanced measurement uses), so the share funnel reports without extra setup.
  useEffect(() => {
    if (open) track("form_start", { form_name: "share_fit", form_id: "product_submission" });
  }, [open]);

  const chooseKind = (value) => {
    track("submission_kind_select", { form_name: "share_fit", kind: value });
    setKind(value);
    // Story and offline-spot share their own composer, not a product link —
    // both skip the size/entries flow and hand off below.
    if (value === "story" || value === "offline_spot") return;
    setEntries([makeEntry()]);
    // Pre-fill the size from the profile so returning users aren't re-asked.
    const savedKey = SIZE_FIELD_BY_KIND[value].key;
    setSize(user?.[savedKey] || "");
  };

  // Closing without a completed share is the single biggest leak in the
  // contribution funnel, and it left no trace at all before this.
  const handleClose = (source) => {
    if (!submitted) {
      track("form_abandon", {
        form_name: "share_fit",
        form_id: "product_submission",
        kind: kind || "not_chosen",
        source,
      });
    }
    onClose();
  };

  // Duplicate verdicts by entry id, filled in by DuplicateLinkNotice. Used only
  // to stop an obviously-doomed submit early — the server is the authority.
  const [dupes, setDupes] = useState({});
  const markDupe = (id, result) =>
    setDupes((prev) => (prev[id] === result || (!prev[id] && !result) ? prev : { ...prev, [id]: result }));

  const updateEntry = (id, patch) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const addEntry = () => setEntries((prev) => [...prev, makeEntry()]);

  const removeEntry = (id) =>
    setEntries((prev) => (prev.length > 1 ? prev.filter((e) => e.id !== id) : prev));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleaned = entries.map((entry) => ({ ...entry, link: entry.link.trim() }));

    for (const entry of cleaned) {
      if (!entry.link) {
        track("form_error", { form_name: "share_fit", reason: "missing_link" });
        toast.error("Please paste a link in every field, or remove the empty ones");
        return;
      }
      let parsed;
      try {
        parsed = new URL(entry.link);
      } catch {
        track("form_error", { form_name: "share_fit", reason: "invalid_url" });
        toast.error(`"${entry.link}" doesn't look like a valid URL`);
        return;
      }
      if (!parsed.protocol.startsWith("http")) {
        track("form_error", { form_name: "share_fit", reason: "bad_protocol" });
        toast.error("Links must start with http or https");
        return;
      }
      if (!entry.fit) {
        track("form_error", { form_name: "share_fit", reason: "missing_fit" });
        toast.error("Please tell us how each one fit");
        return;
      }
    }

    const dupeEntry = cleaned.find((e) => dupes[e.id]);
    if (dupeEntry) {
      track("form_error", {
        form_name: "share_fit",
        reason: "duplicate_link",
        where: dupes[dupeEntry.id]?.where,
      });
      toast.error(
        dupes[dupeEntry.id]?.where === "live"
          ? "That link is already on the site — remove it to continue."
          : "That link is already waiting for review — remove it to continue."
      );
      return;
    }

    if (!size) {
      track("form_error", { form_name: "share_fit", reason: "missing_size" });
      toast.error(`Please select ${isShoes ? "your shoe size" : "your waist size"}`);
      return;
    }

    // Passed validation — this is the real submit attempt, and the denominator
    // for the success/failure split below.
    track("form_submit", {
      form_name: "share_fit",
      form_id: "product_submission",
      kind,
      item_count: cleaned.length,
    });

    setLoading(true);

    // Remember the measurement on the profile so we don't ask again. Best-effort:
    // never block the share if the profile save hiccups.
    if (user && size !== (user[sizeField.key] || "")) {
      try {
        const res = await axios.put(
          `${API}/me/profile`,
          { [sizeField.key]: size },
          { withCredentials: true }
        );
        onUserUpdate?.(res.data.user);
      } catch (err) {
        console.error("Could not save size to profile:", err);
      }
    }

    const results = await Promise.allSettled(
      cleaned.map((entry) =>
        axios.post(
          `${API}/products`,
          { product_link: entry.link, fit_feedback: entry.fit, kind, [sizeField.key]: size },
          { withCredentials: true, headers: { "Content-Type": "application/json" } }
        )
      )
    );

    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - succeeded;

    // GA4's share event, with content_type naming what was contributed. A
    // partial success is still a share, so the counts ride along as params.
    track(succeeded > 0 ? "share" : "share_failed", {
      method: "product_link",
      content_type: kind === "shoes" ? "shoe_fit" : "fit",
      form_name: "share_fit",
      items_succeeded: succeeded,
      items_failed: failed,
    });

    if (succeeded > 0) {
      fireConfetti();
      if (failed > 0) {
        toast.error(`${failed} of ${results.length} couldn't be shared - the rest went through`);
      }
      setSubmitted(true);
      // Hold the thank-you state briefly, then close + refresh discovery
      setTimeout(() => {
        onSuccess();
      }, 2200);
    } else {
      console.error("Error submitting products:", results);
      const detail =
        results[0]?.reason?.response?.data?.detail || "Failed to share. Please try again.";
      toast.error(detail);
      setLoading(false);
    }
  };

  if (!open) return null;

  // "Your Story" hands off to the dedicated composer. Closing it returns to the
  // chooser; a successful post bubbles up and closes the whole modal.
  if (kind === "offline_spot") {
    return (
      <OfflineSpotComposer
        open
        user={user}
        categories={categories}
        onClose={() => setKind(null)}
        onSuccess={onSuccess}
      />
    );
  }

  if (kind === "story") {
    return (
      <StoryComposer open onClose={() => setKind(null)} onSuccess={onSuccess} />
    );
  }

  const multiple = entries.length > 1;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center overflow-hidden"
      data-testid="product-form-overlay"
    >
      {/* Relative wrapper so the fairy-tale sparkles can hug the card edges */}
      <div className="relative w-full max-w-md">
        <FairyDecor />
        <div
          className="relative z-10 bg-white w-full rounded-md border border-[#E4E1D9] p-8 md:p-10 max-h-[90vh] overflow-y-auto"
          data-lenis-prevent
          data-testid="product-form-dialog"
        >
          {submitted ? (
            <div className="text-center py-6" data-testid="submission-success">
              <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-[#F0EFEB] flex items-center justify-center">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2D302A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2
                className="text-3xl md:text-4xl tracking-tighter text-[#2D302A] mb-3"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
                data-testid="submission-success-title"
              >
                Thank you for sharing
              </h2>
              <p
                className="text-sm md:text-base text-[#6B6A68] leading-relaxed"
                data-testid="submission-success-message"
              >
                You just made it easier for another tall person to find their fit.
                That's the whole point of this club.
              </p>
            </div>
          ) : kind === null ? (
            /* Step 1 — what are you sharing? */
            <>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2
                    className="text-3xl tracking-tighter"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                    data-testid="form-title"
                  >
                    Share a Fit
                  </h2>
                  <p className="text-xs md:text-sm text-[#6B6A68] mt-1">
                    What are you sharing?
                  </p>
                </div>
                <button
                  onClick={() => handleClose("kind_picker")}
                  className="text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
                  data-testid="form-close-button"
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4" role="radiogroup" aria-label="What are you sharing?">
                {KIND_OPTIONS.map(({ value, label, hint, Icon, full }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => chooseKind(value)}
                    className={`group flex flex-col items-center justify-center text-center gap-3 px-4 py-8 bg-white border border-[#E4E1D9] rounded-md hover:border-[#2D302A] hover:bg-[#FAF9F5] transition-all ${
                      full ? "col-span-2 py-6" : ""
                    }`}
                    data-testid={`kind-option-${value}`}
                  >
                    <Icon size={40} weight="light" color="#2D302A" className="transition-transform group-hover:scale-110" />
                    <span
                      className="text-xs uppercase tracking-[0.2em] text-[#2D302A]"
                      style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    >
                      {label}
                    </span>
                    <span className="text-[11px] text-[#6B6A68] normal-case tracking-normal leading-snug">
                      {hint}
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            /* Step 2 — paste link(s) + rate the fit */
            <>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <button
                    type="button"
                    onClick={() => setKind(null)}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#9A9A98] hover:text-[#2D302A] transition-colors mb-2"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    data-testid="kind-back-button"
                  >
                    <ArrowLeft size={12} weight="bold" />
                    {isShoes ? "Shoes" : "A Fit"}
                  </button>
                  <h2
                    className="text-3xl tracking-tighter"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                    data-testid="form-title"
                  >
                    {isShoes ? "Share Shoes" : "Share a Fit"}
                  </h2>
                  <p className="text-xs md:text-sm text-[#6B6A68] mt-1">
                    Paste a link - we'll handle the rest.
                  </p>
                </div>
                <button
                  onClick={() => handleClose("link_form")}
                  className="text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
                  data-testid="form-close-button"
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* The sharer's own size — asked once, then pre-filled from
                    their profile. Editable here and in Profile. */}
                <div>
                  <label
                    className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    htmlFor="sharer-size-select"
                    data-testid="sharer-size-label"
                  >
                    {sizeField.label} <span className="text-[#2D302A]">*</span>
                  </label>
                  <select
                    id="sharer-size-select"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    data-testid="sharer-size-select"
                  >
                    <option value="">Select…</option>
                    {sizeField.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {isShoes ? `UK ${opt}` : `${opt} in`}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-[#9A9A98]" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
                    Saved to your profile — we won't ask next time.
                  </p>
                </div>

                {entries.map((entry, index) => (
                  <div
                    key={entry.id}
                    className={multiple ? "border border-[#E4E1D9] rounded-md p-4 space-y-4" : "space-y-6"}
                    data-testid={`fit-entry-${index}`}
                  >
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label
                          className="block text-xs uppercase tracking-[0.2em] text-[#6B6A68]"
                          style={{ fontFamily: "IBM Plex Mono, monospace" }}
                          data-testid="product-link-label"
                          htmlFor={`product-link-input-${entry.id}`}
                        >
                          {multiple ? `Product Link ${index + 1}` : "Product Link"}
                        </label>
                        {multiple && (
                          <button
                            type="button"
                            onClick={() => removeEntry(entry.id)}
                            className="text-[#9A9A98] hover:text-red-600 transition-colors"
                            aria-label={`Remove link ${index + 1}`}
                            data-testid={`remove-entry-${index}`}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      <input
                        id={`product-link-input-${entry.id}`}
                        type="url"
                        value={entry.link}
                        onChange={(e) => updateEntry(entry.id, { link: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
                        placeholder="https://..."
                        autoFocus={index === 0}
                        data-testid="product-link-input"
                      />
                      <DuplicateLinkNotice
                        url={entry.link}
                        onResult={(r) => markDupe(entry.id, r)}
                      />
                    </div>

                    {/* Fit feedback (required, per product) */}
                    <div>
                      <label
                        className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        data-testid="fit-feedback-label"
                      >
                        How did it fit? <span className="text-[#2D302A]">*</span>
                      </label>
                      <div className="space-y-2" role="radiogroup" aria-label="How did it fit">
                        {fitOptions.map((opt) => {
                          const active = entry.fit === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              onClick={() => updateEntry(entry.id, { fit: opt.value })}
                              className={`w-full text-left px-4 py-3 border rounded-md transition-all ${
                                active
                                  ? "bg-[#2D302A] text-white border-[#2D302A]"
                                  : "bg-white text-[#1A1A1A] border-[#E4E1D9] hover:border-[#2D302A]"
                              }`}
                              style={{ fontFamily: "IBM Plex Mono, monospace" }}
                              data-testid={`fit-option-${opt.value}`}
                            >
                              <span className="text-xs md:text-sm tracking-wide">{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addEntry}
                  className="w-full flex items-center justify-center gap-2 border border-dashed border-[#C9C5BA] text-[#2D302A] px-4 py-3 rounded-md hover:bg-[#FAF9F5] transition-colors"
                  style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  data-testid="add-link-button"
                >
                  <Plus size={16} weight="bold" />
                  <span className="text-xs uppercase tracking-[0.2em]">Add another link</span>
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#2D302A] text-white px-6 py-4 rounded-md hover:bg-[#1A1C18] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  data-testid="submit-fit-button"
                >
                  <span className="text-xs uppercase tracking-[0.2em]">
                    {loading
                      ? "Sharing..."
                      : multiple
                      ? `Share ${entries.length} ${isShoes ? "Pairs" : "Fits"}`
                      : isShoes
                      ? "Share Shoes"
                      : "Share Fit"}
                  </span>
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSubmissionForm;
